/**
 * CognixToolBuilder
 *
 * Modal form for creating or editing a Cognix Tool.
 * - Step 1: Select datasource type (DB, API, or EMAIL) and pick from existing datasources
 * - Step 2: Fill in tool name, description, and type-specific config
 *           DB → SQL query  |  API → endpoint/operation/payload  |  EMAIL → subject/body templates
 * - Parameters are auto-detected from $$varname placeholders
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { cognixToolService } from '../services/cognixToolService';
import { emailDatasourceService } from '../services/emailDatasourceService';
import { listKnowledgeBases } from '../services/knowledgeBaseService';
import { API_CONFIG } from '../config/api';

const API_URL = API_CONFIG.BASE_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('authToken')}`
});

// Extract $$param names from a string
const extractParams = (str) => {
  if (!str) return [];
  const matches = [...new Set(str.match(/\$\$([a-zA-Z_][a-zA-Z0-9_]*)/g) || [])];
  return matches.map(m => m.replace('$$', ''));
};

const OPERATIONS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

const defaultForm = {
  tool_name: '',
  tool_description: '',
  datasource_type: 'DB',
  datasource_id: '',
  // DB-specific
  query: '',
  read_only: true,
  // API-specific
  endpoint: '',
  operation: 'GET',
  payload: '',
  body_format: 'json',
  headers: '',
  // EMAIL-specific
  subject_template: '',
  body_template: '',
  // KNOWLEDGE_BASE-specific
  kb_id: '',
  top_k: 5
};

const CognixToolBuilder = ({ existingTool, onClose, onSuccess, embedded = false, onToolCreated }) => {
  const isEdit = Boolean(existingTool);

  const [form, setForm] = useState(() => {
    if (isEdit) {
      const cfg = existingTool.tool_config || {};
      return {
        tool_name: existingTool.tool_name || '',
        tool_description: existingTool.tool_description || '',
        datasource_type: existingTool.datasource_type || 'DB',
        datasource_id: String(existingTool.datasource_id || ''),
        query: cfg.query || '',
        read_only: cfg.read_only !== false,
        endpoint: cfg.endpoint || '',
        operation: cfg.operation || 'GET',
        payload: cfg.payload ? JSON.stringify(cfg.payload, null, 2) : '',
        body_format: cfg.body_format || 'json',
        headers: cfg.headers ? JSON.stringify(cfg.headers, null, 2) : '',
        subject_template: cfg.subject_template || '',
        body_template: cfg.body_template || '',
        kb_id: cfg.kb_id || '',
        top_k: cfg.top_k || 5
      };
    }
    return { ...defaultForm };
  });

  const [dbDatasources, setDbDatasources] = useState([]);
  const [apiDatasources, setApiDatasources] = useState([]);
  const [emailDatasources, setEmailDatasources] = useState([]);
  const [kbList, setKbList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dsLoading, setDsLoading] = useState(true);
  const [error, setError] = useState('');
  const [detectedParams, setDetectedParams] = useState([]);
  const [toolTemplates, setToolTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Load all datasource types on mount
  useEffect(() => {
    const load = async () => {
      try {
        setDsLoading(true);
        const [dbRes, apiRes, emailRes, kbRes] = await Promise.all([
          axios.get(`${API_URL}/api/datasources`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/api-datasources`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
          emailDatasourceService.list().catch(() => ({ datasources: [] })),
          listKnowledgeBases().catch(() => ({ knowledge_bases: [] }))
        ]);
        setDbDatasources(Array.isArray(dbRes.data) ? dbRes.data : []);
        const apiData = apiRes.data;
        setApiDatasources(
          Array.isArray(apiData) ? apiData :
          Array.isArray(apiData?.datasources) ? apiData.datasources : []
        );
        setEmailDatasources(emailRes?.datasources || []);
        setKbList((kbRes?.knowledge_bases || []).filter(kb => kb.status === 'ready'));
      } catch (e) {
        console.error('Failed to load datasources', e);
      } finally {
        setDsLoading(false);
      }
    };
    load();
  }, []);

  // Auto-detect parameters when type-specific fields change
  useEffect(() => {
    if (form.datasource_type === 'KNOWLEDGE_BASE') {
      setDetectedParams(['query']);
      return;
    }
    let src = '';
    if (form.datasource_type === 'DB') {
      src = form.query;
    } else if (form.datasource_type === 'API') {
      src = `${form.endpoint} ${form.payload}`;
    } else if (form.datasource_type === 'EMAIL') {
      src = `${form.subject_template} ${form.body_template}`;
    }
    const detected = extractParams(src);
    if (form.datasource_type === 'EMAIL') {
      const emailCore = ['to_email', 'subject', 'body'];
      setDetectedParams([...new Set([...emailCore, ...detected])]);
    } else {
      setDetectedParams(detected);
    }
  }, [form.datasource_type, form.query, form.endpoint, form.payload, form.subject_template, form.body_template]);

  // Fetch provider tool templates when an API datasource is selected
  useEffect(() => {
    if (form.datasource_type !== 'API' || !form.datasource_id) {
      setToolTemplates([]);
      setSelectedTemplate(null);
      return;
    }
    axios.get(`${API_URL}/api/api-datasources/${form.datasource_id}/tool-templates`, { headers: getAuthHeaders() })
      .then(res => setToolTemplates(res.data?.templates || []))
      .catch(() => setToolTemplates([]));
  }, [form.datasource_type, form.datasource_id]);

  const applyTemplate = (tpl) => {
    setSelectedTemplate(tpl.key);
    setForm(prev => ({
      ...prev,
      endpoint: tpl.endpoint || '',
      operation: tpl.operation || 'GET',
      payload: tpl.payload ? JSON.stringify(tpl.payload, null, 2) : '',
      body_format: tpl.body_format || 'json',
      // Suggest tool name/desc only if user hasn't filled them yet
      tool_name: prev.tool_name || tpl.key,
      tool_description: prev.tool_description || tpl.description || '',
    }));
  };

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleDatasourceTypeChange = (type) => {
    setForm(prev => ({ ...prev, datasource_type: type, datasource_id: '' }));
    setError('');
  };

  const buildToolConfig = () => {
    if (form.datasource_type === 'KNOWLEDGE_BASE') {
      return {
        kb_id: form.kb_id,
        top_k: parseInt(form.top_k) || 5,
        Parameter: ['query']
      };
    }
    if (form.datasource_type === 'DB') {
      return {
        query: form.query.trim(),
        read_only: form.read_only,
        Parameter: detectedParams
      };
    }
    if (form.datasource_type === 'EMAIL') {
      return {
        subject_template: form.subject_template.trim() || '$$subject',
        body_template: form.body_template.trim() || '$$body',
        Parameter: detectedParams
      };
    }
    // API
    const cfg = {
      endpoint: form.endpoint.trim(),
      operation: form.operation,
      body_format: form.body_format || 'json',
      Parameter: detectedParams
    };
    if (form.payload.trim()) {
      try {
        cfg.payload = JSON.parse(form.payload);
      } catch {
        throw new Error('Payload is not valid JSON');
      }
    }
    if (form.headers.trim()) {
      try {
        cfg.headers = JSON.parse(form.headers);
      } catch {
        throw new Error('Headers is not valid JSON');
      }
    }
    return cfg;
  };

  // Live sanitizer — only strips invalid characters and collapses consecutive
  // underscores. We intentionally do NOT strip leading/trailing underscores here
  // because this runs on every keystroke; stripping trailing underscores makes it
  // impossible to type "get_customer" (the _ gets removed the instant it's typed).
  // Leading/trailing underscores are stripped on submit instead.
  const sanitizeToolName = (value) =>
    value.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');

  const validate = () => {
    if (!form.tool_name.trim()) return 'Tool name is required';
    if (!/^[a-zA-Z0-9_-]{1,128}$/.test(form.tool_name)) return 'Tool name can only contain letters, numbers, underscores and hyphens (no spaces)';
    if (!form.tool_description.trim()) return 'Tool description is required';
    if (form.datasource_type === 'KNOWLEDGE_BASE') {
      if (!form.kb_id) return 'Please select a Knowledge Base';
      return null;
    }
    if (!form.datasource_id) return 'Please select a datasource';
    if (form.datasource_type === 'DB' && !form.query.trim()) return 'SQL query is required';
    if (form.datasource_type === 'API' && !form.endpoint.trim()) return 'Endpoint is required';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    let toolConfig;
    try {
      toolConfig = buildToolConfig();
    } catch (err) {
      setError(err.message);
      return;
    }

    // Embedded (workflow inline) mode — return config to parent, do NOT save to DB
    if (embedded && !isEdit) {
      onToolCreated({
        tool_name: form.tool_name.trim().replace(/^_+|_+$/g, ''),
        tool_description: form.tool_description.trim(),
        datasource_type: form.datasource_type,
        datasource_id: form.datasource_type === 'KNOWLEDGE_BASE' ? 0 : parseInt(form.datasource_id, 10),
        tool_config: toolConfig
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        const result = await cognixToolService.updateTool(existingTool.tool_uuid, {
          tool_name: form.tool_name.trim().replace(/^_+|_+$/g, ''),
          tool_description: form.tool_description.trim(),
          tool_config: toolConfig
        });
        if (!result?.success) throw new Error(result?.error || 'Update failed');
      } else {
        const result = await cognixToolService.createTool({
          tool_name: form.tool_name.trim().replace(/^_+|_+$/g, ''),
          tool_description: form.tool_description.trim(),
          datasource_type: form.datasource_type,
          datasource_id: form.datasource_type === 'KNOWLEDGE_BASE' ? 0 : parseInt(form.datasource_id, 10),
          tool_config: toolConfig
        });
        if (!result?.success) throw new Error(result?.error || 'Create failed');
      }
      onSuccess();
    } catch (err) {
      setError(err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const activeDatasources =
    form.datasource_type === 'DB' ? dbDatasources :
    form.datasource_type === 'EMAIL' ? emailDatasources :
    apiDatasources;

  const selectedDs = activeDatasources.find(ds => String(ds.id) === form.datasource_id);

  const formBody = (
    <form onSubmit={handleSubmit} className={embedded ? 'ctb-embedded-form' : 'flex flex-col flex-1 min-h-0'}>
      <div className={embedded ? 'ctb-embedded-body' : 'overflow-y-auto flex-1 p-6 space-y-5'}>
            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* ── Section 1: Connection ── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                1. Connection
              </h3>

              {/* Datasource Type Toggle */}
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { key: 'DB', label: 'Database (JDBC)' },
                  { key: 'API', label: 'REST API' },
                  { key: 'EMAIL', label: 'Email (SMTP)' },
                  { key: 'KNOWLEDGE_BASE', label: 'Knowledge Base' }
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleDatasourceTypeChange(key)}
                    disabled={isEdit}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors
                      ${form.datasource_type === key
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}
                      ${isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Datasource Selector (hidden for KB type) */}
              {form.datasource_type !== 'KNOWLEDGE_BASE' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.datasource_type === 'DB' ? 'Select Database' :
                   form.datasource_type === 'EMAIL' ? 'Select Email Connection' :
                   'Select API Datasource'}
                </label>
                {dsLoading ? (
                  <div className="text-sm text-gray-500 py-2">Loading datasources...</div>
                ) : activeDatasources.length === 0 ? (
                  <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                    No {form.datasource_type === 'DB' ? 'database' : form.datasource_type === 'EMAIL' ? 'email' : 'API'} datasources found.
                    {form.datasource_type === 'EMAIL'
                      ? ' Go to Datasources → Email Connections to add one first.'
                      : ' Please add one in the Datasources section first.'}
                  </div>
                ) : (
                  <select
                    value={form.datasource_id}
                    onChange={e => set('datasource_id', e.target.value)}
                    disabled={isEdit}
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500
                      ${isEdit ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                  >
                    <option value="">-- Select a datasource --</option>
                    {activeDatasources.map(ds => (
                      <option key={ds.id} value={String(ds.id)}>
                        {ds.name || ds.datasource_name}
                        {form.datasource_type === 'DB' && ds.db_type ? ` (${ds.db_type})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {selectedDs && form.datasource_type === 'DB' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedDs.host}:{selectedDs.port} / {selectedDs.database_name}
                  </p>
                )}
                {selectedDs && form.datasource_type === 'API' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedDs.base_url} · auth: {selectedDs.auth_method || 'none'}
                  </p>
                )}
                {selectedDs && form.datasource_type === 'EMAIL' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedDs.smtp_host}:{selectedDs.smtp_port} · {selectedDs.smtp_username}
                  </p>
                )}
              </div>
              )}
            </div>

            {/* ── Quick Templates (API only) ── */}
            {form.datasource_type === 'API' && form.datasource_id && toolTemplates.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Quick Templates
                </h3>
                <p className="text-xs text-gray-400 mb-3">
                  Pick a pre-built action — endpoint, method and payload will be filled in automatically.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {toolTemplates.map(tpl => (
                    <button
                      key={tpl.key}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all
                        ${selectedTemplate === tpl.key
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                          : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-gray-50 text-gray-700'
                        }`}
                    >
                      <div className="font-medium leading-snug">{tpl.label}</div>
                      <div className="text-xs text-gray-400 mt-0.5 truncate">{tpl.description}</div>
                    </button>
                  ))}
                </div>
                {selectedTemplate && (
                  <button
                    type="button"
                    onClick={() => { setSelectedTemplate(null); setForm(prev => ({ ...prev, endpoint: '', operation: 'GET', payload: '' })); }}
                    className="mt-2 text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    Clear template
                  </button>
                )}
              </div>
            )}

            {/* ── Section 2: Tool Definition ── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                2. Tool Definition
              </h3>

              {/* Name */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tool Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.tool_name}
                  onChange={e => set('tool_name', sanitizeToolName(e.target.value))}
                  placeholder={
                    form.datasource_type === 'EMAIL'
                      ? 'e.g. send_order_notification'
                      : 'e.g. get_customer_by_id'
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-400 mt-1">Letters, numbers, underscores and hyphens only — no spaces. Spaces are auto-converted to underscores.</p>
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.tool_description}
                  onChange={e => set('tool_description', e.target.value)}
                  placeholder={
                    form.datasource_type === 'EMAIL'
                      ? 'Send an email notification to a given address with subject and body...'
                      : 'Describe what this tool does and when the agent should use it...'
                  }
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {/* DB-specific fields */}
              {form.datasource_type === 'DB' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SQL Query <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={form.query}
                    onChange={e => set('query', e.target.value)}
                    placeholder={form.read_only
                      ? `SELECT * FROM customers WHERE id = $$customer_id`
                      : `INSERT INTO commands (customer_id, type) VALUES ($$customer_id, 'REBOOT')`}
                    rows={5}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Use <code className="bg-gray-100 px-1 rounded">$$param_name</code> for dynamic parameters.
                  </p>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.read_only}
                      onChange={e => set('read_only', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Read-only (SELECT only)</span>
                    {!form.read_only && (
                      <span className="text-xs text-amber-600 font-medium">⚠ Write mode — INSERT/UPDATE/DELETE allowed</span>
                    )}
                  </label>
                </div>
              )}

              {/* API-specific fields */}
              {form.datasource_type === 'API' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Endpoint <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.endpoint}
                        onChange={e => set('endpoint', e.target.value)}
                        placeholder="/api/v1/resource"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Method <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={form.operation}
                        onChange={e => set('operation', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {OPERATIONS.map(op => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Payload (optional)
                      </label>
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                        {['json', 'form'].map(fmt => (
                          <button
                            key={fmt}
                            type="button"
                            onClick={() => set('body_format', fmt)}
                            className={`px-2.5 py-0.5 rounded-md text-xs font-medium transition-colors ${
                              (form.body_format || 'json') === fmt
                                ? 'bg-white text-indigo-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {fmt === 'json' ? 'JSON' : 'Form'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={form.payload}
                      onChange={e => set('payload', e.target.value)}
                      placeholder={form.body_format === 'form'
                        ? `{\n  "To": "$$to",\n  "Body": "$$message"\n}`
                        : `{\n  "userId": "$$userId"\n}`}
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Use <code className="bg-gray-100 px-1 rounded">$$param_name</code> for dynamic values.
                      {form.body_format === 'form' && <span className="ml-1 text-amber-600">Sent as URL-encoded form (e.g. Twilio).</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Extra Headers (JSON, optional)
                    </label>
                    <textarea
                      value={form.headers}
                      onChange={e => set('headers', e.target.value)}
                      placeholder={`{\n  "Content-Type": "application/json"\n}`}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              )}

              {/* EMAIL-specific fields */}
              {form.datasource_type === 'EMAIL' && (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    <strong>Core parameters</strong> — the agent will always supply:
                    <code className="mx-1 px-1 bg-amber-100 rounded">to_email</code>,
                    <code className="mx-1 px-1 bg-amber-100 rounded">subject</code>,
                    <code className="mx-1 px-1 bg-amber-100 rounded">body</code>.
                    You can also add extra <code className="mx-1 px-1 bg-amber-100 rounded">$$param</code> placeholders
                    in the templates below to auto-fill common values.
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject Template (optional)
                    </label>
                    <input
                      type="text"
                      value={form.subject_template}
                      onChange={e => set('subject_template', e.target.value)}
                      placeholder="Order $$order_id shipped! (leave blank to let agent decide)"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Leave blank to let the agent compose the subject dynamically.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Body Template (optional)
                    </label>
                    <textarea
                      value={form.body_template}
                      onChange={e => set('body_template', e.target.value)}
                      placeholder={"Hi $$customer_name,\n\nYour order $$order_id has been shipped.\n\nThank you!"}
                      rows={5}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Leave blank to let the agent compose the body dynamically.
                      HTML is supported.
                    </p>
                  </div>
                </div>
              )}

              {/* KNOWLEDGE_BASE-specific fields */}
              {form.datasource_type === 'KNOWLEDGE_BASE' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Knowledge Base <span className="text-red-500">*</span>
                    </label>
                    {dsLoading ? (
                      <div className="text-sm text-gray-500 py-2">Loading knowledge bases...</div>
                    ) : kbList.length === 0 ? (
                      <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                        No ready knowledge bases found. Go to Knowledge Base, add files and process them first.
                      </div>
                    ) : (
                      <select
                        value={form.kb_id}
                        onChange={e => set('kb_id', e.target.value)}
                        disabled={isEdit}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">-- Select a Knowledge Base --</option>
                        {kbList.map(kb => (
                          <option key={kb.id} value={kb.id}>
                            {kb.name} ({kb.total_chunks} chunks)
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Top K Results
                    </label>
                    <input
                      type="number" min="1" max="20"
                      value={form.top_k}
                      onChange={e => set('top_k', parseInt(e.target.value) || 5)}
                      className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Number of relevant chunks to return per query (1–20)</p>
                  </div>
                  <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-800">
                    The agent will call this tool with a <code className="mx-1 px-1 bg-indigo-100 rounded">query</code> parameter and receive the most relevant document chunks.
                  </div>
                </div>
              )}

              {/* Auto-detected parameters */}
              {detectedParams.length > 0 && (
                <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs font-medium text-indigo-700 mb-1">Parameters the agent will supply:</p>
                  <div className="flex flex-wrap gap-1">
                    {detectedParams.map(p => (
                      <span key={p} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-white border border-indigo-300 text-indigo-800">
                        {p}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-indigo-500 mt-1">
                    The agent will gather these values before calling this tool.
                  </p>
                </div>
              )}
            </div>

            {/* end scrollable body */}
            </div>

            {/* Footer */}
            <div className={embedded ? 'ctb-embedded-footer' : 'flex justify-end gap-3 px-6 py-4 border-t bg-white flex-shrink-0'}>
              {!embedded && (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : embedded ? 'Add Tool' : 'Create Tool')}
              </button>
            </div>
          </form>
  );

  if (embedded) return formBody;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-40" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl z-10 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEdit ? 'Edit Tool' : 'Create Tool'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Build a reusable tool on top of an existing datasource
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>
          {formBody}
        </div>
      </div>
    </div>
  );
};

export default CognixToolBuilder;
